import _ from 'lodash';

/**
 * Convierte una cadena de texto a formato kebab-case.
 *
 * Elimina espacios al inicio y al final, separa las palabras por espacios,
 * convierte todo a minúsculas y une las palabras con guiones medios.
 *
 * @param {string} value - Cadena de texto a convertir.
 * @returns {string} Cadena convertida a formato kebab-case.
 *
 * @example
 * stringToKebabCase('Hola Mundo Test');
 * // retorna: 'hola-mundo-test'
 *
 * @example
 * stringToKebabCase('  Reportes Financieros  ');
 * // retorna: 'reportes-financieros'
 */
export function stringToKebabCase(value) {
  const trimmed = value.trim();
  let sanitized = trimmed.split(/\s+/);
  sanitized = sanitized.map((word) => word.toLowerCase());
  return sanitized.join('-');
}

/**
 * Convierte una cadena de texto a formato snake_case.
 *
 * Elimina espacios al inicio y al final, separa las palabras por espacios,
 * convierte todo a minúsculas y une las palabras con guiones bajos.
 *
 * @param {string} value - Cadena de texto a convertir.
 * @returns {string} Cadena convertida a formato snake_case.
 *
 * @example
 * stringToSnakeCase('Hola Mundo Test');
 * // retorna: 'hola_mundo_test'
 *
 * @example
 * stringToSnakeCase('  Reportes Financieros  ');
 * // retorna: 'reportes_financieros'
 */
export function stringToSnakeCase(value) {
  const trimmed = value.trim();
  let sanitized = trimmed.split(/\s+/);
  sanitized = sanitized.map((word) => word.toLowerCase());
  return sanitized.join('_');
}

/**
 * Convierte una cadena de texto a formato camelCase.
 *
 * Elimina espacios al inicio y al final, separa las palabras por espacios,
 * convierte todo a minúsculas, mantiene la primera palabra en minúsculas
 * y capitaliza la primera letra de las palabras siguientes.
 *
 * @param {string} value - Cadena de texto a convertir.
 * @returns {string} Cadena convertida a formato camelCase.
 *
 * @example
 * stringToCamelCase('Hola Mundo Test');
 * // retorna: 'holaMundoTest'
 *
 * @example
 * stringToCamelCase('  Reportes Financieros  ');
 * // retorna: 'reportesFinancieros'
 */
export function stringToCamelCase(value) {
  const trimmed = value.trim();
  let sanitized = trimmed.split(/\s+/);
  sanitized = sanitized.map((word) => word.toLowerCase());

  const firstWord = sanitized[0] || '';

  const restOfWords = sanitized
    .slice(1)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  return firstWord + restOfWords;
}

/**
 * Normaliza una cadena de texto.
 *
 * Reemplaza múltiples espacios consecutivos por un solo espacio,
 * elimina espacios al inicio y al final, y convierte toda la cadena
 * a minúsculas.
 *
 * @param {string} str - Cadena de texto a normalizar.
 * @returns {string} Cadena normalizada.
 *
 * @example
 * normalizeString('  Hola    Mundo  ');
 * // retorna: 'hola mundo'
 *
 * @example
 * normalizeString('REPORTES   FINANCIEROS');
 * // retorna: 'reportes financieros'
 */
export function normalizeString(str) {
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Realiza una combinación profunda parcial de múltiples objetos.
 *
 * Usa `_.mergeWith` de Lodash para combinar los objetos recibidos,
 * pero sobrescribe completamente ciertos valores en lugar de fusionarlos.
 *
 * Reglas especiales:
 * - Si el siguiente valor es un array, reemplaza el valor anterior.
 * - Si el valor anterior es un array y el siguiente es un objeto plano,
 *   reemplaza el valor anterior.
 * - Si el valor anterior es un objeto plano y el siguiente es un array,
 *   reemplaza el valor anterior.
 * - En los demás casos, Lodash aplica su comportamiento normal de merge profundo.
 *
 * @param {...Object} args - Objetos que serán combinados.
 * @returns {Object} Nuevo objeto resultante de la combinación profunda parcial.
 *
 * @example
 * partialMerge(
 *   { user: { name: 'Juan', roles: ['admin'] } },
 *   { user: { age: 30, roles: ['editor'] } }
 * );
 * // retorna:
 * // {
 * //   user: {
 * //     name: 'Juan',
 * //     age: 30,
 * //     roles: ['editor']
 * //   }
 * // }
 *
 * @example
 * partialMerge(
 *   { filters: ['active'] },
 *   { filters: { status: 'active' } }
 * );
 * // retorna:
 * // {
 * //   filters: { status: 'active' }
 * // }
 */
export function partialMerge(...args) {
  return _.mergeWith({}, ...args, (prevValue, nextValue) => {
    const isPrevArray = Array.isArray(prevValue);
    const isNextArray = Array.isArray(nextValue);

    const isPrevObject = _.isPlainObject(prevValue);
    const isNextObject = _.isPlainObject(nextValue);

    if (isNextArray || (isPrevArray && isNextObject) || (isPrevObject && isNextArray)) {
      return _.cloneDeep(nextValue);
    }

    return undefined;
  });
}
